(() => {
  'use strict';
  if (window.BisonVideoSharing) return;
  window.BisonVideoSharing = true;
  const selector = 'video, iframe[src*="youtube.com/embed"], iframe[src*="youtube-nocookie.com/embed"], iframe[src*="player.vimeo.com"]';
  const entries = new Map();
  let panel, opener, active, status;
  const source = media => media.getAttribute('src') || media.querySelector('source[src]')?.getAttribute('src') || media.getAttribute('data-src') || '';
  const keyFor = media => {
    if (media.dataset.bisonShareId) return media.dataset.bisonShareId;
    const src = source(media);
    if (!src) return '';
    // CDN signatures and quality variants do not change the shared destination.
    const path = new URL(src, location.href).pathname.replace(/\.(?:hd|sd|[0-9]+p)\./g, '.');
    let hash = 2166136261;
    for (const char of path) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
    return (hash >>> 0).toString(36);
  };
  const payload = entry => {
    const url = new URL(location.pathname, window.BisonVideoShareOrigin || location.origin);
    // Keep draft-theme links in the preview while the feature is being tested.
    // Published themes always share the public storefront URL.
    const theme = window.Shopify?.theme;
    const previewId = new URL(location.href).searchParams.get('preview_theme_id');
    if (theme?.id && theme.role && theme.role !== 'main') {
      url.searchParams.set('preview_theme_id', String(theme.id));
    } else if (!theme && previewId) {
      url.searchParams.set('preview_theme_id', previewId);
    }
    url.hash = 'bison-video-' + entry.key;
    return { title: entry.media.dataset.bisonShareTitle || entry.media.title || entry.media.getAttribute('aria-label') || document.title.replace(/\s*[|–-]\s*BISON.*$/i, '') || 'BISON video', url: url.href };
  };
  const track = (entry, method) => {
    // Use the store's existing analytics pipeline; never load a second tracker.
    window.dataLayer?.push({ event: 'share', method, content_type: 'video', item_id: entry.key, video_title: payload(entry).title });
  };
  const close = (restore = false) => {
    if (!panel) return;
    panel.hidden = true;
    opener?.setAttribute('aria-expanded', 'false');
    if (restore && opener?.isConnected) opener.focus();
  };
  const position = () => {
    if (!panel || panel.hidden || !opener?.isConnected) return;
    const box = opener.getBoundingClientRect();
    panel.style.left = Math.max(8, Math.min(box.right - panel.offsetWidth, innerWidth - panel.offsetWidth - 8)) + 'px';
    panel.style.top = Math.max(8, Math.min(box.bottom + 8, innerHeight - panel.offsetHeight - 8)) + 'px';
  };
  const nativeShare = async entry => {
    try { await navigator.share(payload(entry)); track(entry, 'native'); close(true); }
    catch (error) { if (error.name !== 'AbortError') show(entry); }
  };
  const createPanel = () => {
    panel = document.createElement('div');
    panel.id = 'BisonVideoShareMenu';
    panel.className = 'bison-video-share-menu';
    panel.hidden = true;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Share this video');
    const heading = document.createElement('strong');
    heading.textContent = 'Share this video';
    panel.append(heading);
    const copy = document.createElement('button');
    copy.type = 'button'; copy.textContent = 'Copy link';
    copy.addEventListener('click', async () => {
      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(payload(active).url);
        status.textContent = 'Link copied!'; track(active, 'copy_link');
      } catch (_) {
        const input = panel.querySelector('input');
        input.hidden = false; input.value = payload(active).url;
        input.focus(); input.select();
        status.textContent = 'Select and copy this link.';
        position();
      }
    });
    panel.append(copy);
    for (const [label, method] of [['Facebook','facebook'],['WhatsApp','whatsapp'],['X','x'],['Email','email']]) {
      const link = document.createElement('a');
      link.textContent = label; link.dataset.shareMethod = method;
      if (method !== 'email') { link.target = '_blank'; link.rel = 'noopener noreferrer'; }
      link.addEventListener('click', () => { track(active, method); close(); });
      panel.append(link);
    }
    if (navigator.share) {
      const more = document.createElement('button'); more.type = 'button'; more.textContent = 'More…';
      more.addEventListener('click', () => nativeShare(active)); panel.append(more);
    }
    const input = document.createElement('input'); input.type = 'text'; input.readOnly = true; input.hidden = true;
    input.setAttribute('aria-label', 'Video link'); panel.append(input);
    status = document.createElement('p'); status.setAttribute('role', 'status'); panel.append(status);
    const dismiss = document.createElement('button'); dismiss.type = 'button'; dismiss.textContent = 'Close';
    dismiss.addEventListener('click', () => close(true)); panel.append(dismiss);
    document.body.append(panel);
  };
  function show(entry) {
    if (!panel) createPanel();
    close(); active = entry; opener = entry.button;
    // Fullscreen only displays descendants of the fullscreen element.
    const surface = document.fullscreenElement || document.webkitFullscreenElement ||
      entry.media.closest('.bison-landscape-overlay, .bison-video-fullscreen-fallback') || document.body;
    surface.append(panel);
    const data = payload(entry), url = encodeURIComponent(data.url), title = encodeURIComponent(data.title);
    const links = {
      facebook: 'https://www.facebook.com/sharer/sharer.php?u=' + url,
      whatsapp: 'https://wa.me/?text=' + title + '%20' + url,
      x: 'https://twitter.com/intent/tweet?text=' + title + '&url=' + url,
      email: 'mailto:?subject=' + title + '&body=' + title + '%0A%0A' + url
    };
    panel.querySelectorAll('[data-share-method]').forEach(link => { link.href = links[link.dataset.shareMethod]; });
    status.textContent = ''; panel.querySelector('input').hidden = true;
    panel.hidden = false; opener.setAttribute('aria-expanded', 'true');
    position(); panel.querySelector('button').focus();
  }
  const enhance = media => {
    // Homepage background, hero circles and comparison clips are decorative loops.
    if (media.matches('.bison-video-hero__media') || media.closest('.bnl-hero') ||
        media.closest('.bison-video-hero__demo-media, .bison-compare__media, .bnl-compare__media')) return;
    // Native title tooltips appear over the picture after a short hover.
    // Keep the video's name for assistive technology and share messages.
    if (media.tagName === 'VIDEO' && media.hasAttribute('title')) {
      const title = media.getAttribute('title');
      if (title) {
        media.dataset.bisonShareTitle = title;
        if (!media.hasAttribute('aria-label')) media.setAttribute('aria-label', title);
      }
      media.removeAttribute('title');
    }
    if (entries.has(media)) {
      // Product controls may be added after sharing initializes.
      const entry = entries.get(media);
      const controls = media.parentElement?.querySelector(':scope > .bison-product-video-controls, :scope > .bison-video-controls');
      if (controls && entry.button.parentElement !== controls) {
        controls.append(entry.button);
        entry.button.classList.add('bison-video-share--grouped');
      }
      return;
    }
    const key = keyFor(media);
    if (!key || !media.parentElement) return;
    const host = media.parentElement;
    host.classList.add('bison-video-share-host');
    const entry = { media, key };
    const button = document.createElement('button');
    button.type = 'button'; button.className = 'bison-video-share'; button.textContent = 'SHARE';
    button.setAttribute('aria-label', 'Share this video');
    button.setAttribute('aria-haspopup', 'dialog'); button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', 'BisonVideoShareMenu');
    const controls = host.querySelector(':scope > .bison-product-video-controls, :scope > .bison-video-controls');
    if (controls) { controls.append(button); button.classList.add('bison-video-share--grouped'); }
    else host.append(button);
    if (media.classList.contains('bison-video-hero__media') || media.closest('.bnl-hero')) button.classList.add('bison-video-share--background');
    entry.button = button; entries.set(media, entry);
    if (media.tagName === 'VIDEO' && !media.loop &&
        !button.classList.contains('bison-video-share--background')) {
      media.dataset.bisonClickPlayback = 'true';
      let clickTimer = null;
      entry.cancelClick = () => { clearTimeout(clickTimer); clickTimer = null; };
      media.addEventListener('click', event => {
        // Native timeline, sound and menu controls occupy the lower strip.
        const box = media.getBoundingClientRect();
        if (media.controls && event.clientY >= box.bottom - 64) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        entry.cancelClick();
        if (Date.now() - (entry.touchFullscreenAt || 0) < 450 || event.detail > 1) return;
        const toggle = () => {
          if (media.paused) media.play().catch(() => {});
          else media.pause();
        };
        // Touch playback must remain inside the gesture for iOS audio.
        if (matchMedia('(pointer: coarse)').matches) toggle();
        else clickTimer = setTimeout(toggle, 250);
      }, true);
    }
    // Expand the whole player so custom SHARE and caption controls stay visible.
    // Keep native playback, timeline and volume controls.
    if (media.tagName === 'VIDEO' && media.controls) {
      host.classList.add('bison-video-fullscreen-host');
      media.controlsList?.add('nofullscreen');
      const expand = document.createElement('button');
      expand.type = 'button';
      expand.className = 'bison-video-fullscreen';
      expand.setAttribute('aria-label', 'Enter full screen');
      expand.title = 'Full screen';
      expand.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M8 3H3v5M16 3h5v5M3 16v5h5M21 16v5h-5" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
      host.append(expand);
      const exit = document.createElement('button');
      exit.type = 'button'; exit.className = 'bison-video-exit';
      exit.setAttribute('aria-label', 'Exit full screen');
      exit.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
      exit.hidden = true;
      host.append(exit);
      let placement = null;
      const restore = () => {
        if (!placement) return;
        placement.placeholder.replaceWith(host);
        host.classList.remove('bison-video-fullscreen-fallback');
        placement = null;
        close();
        sync();
      };
      const sync = () => {
        const fullscreen = document.fullscreenElement || document.webkitFullscreenElement;
        const current = fullscreen === host || !!placement;
        expand.setAttribute('aria-label', current ? 'Exit full screen' : 'Enter full screen');
        expand.title = current ? 'Exit full screen' : 'Full screen';
        expand.classList.toggle('is-fullscreen', current);
        exit.hidden = !current;
        if (panel && !panel.hidden && opener === button) {
          (fullscreen || document.body).append(panel);
          position();
        }
      };
      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || (panel && !panel.hidden)) return;
        if (placement) restore();
        else if ((document.fullscreenElement || document.webkitFullscreenElement) === host)
          (document.exitFullscreen || document.webkitExitFullscreen).call(document);
      });
      document.addEventListener('fullscreenchange', sync);
      document.addEventListener('webkitfullscreenchange', sync);
      const toggleFullscreen = async event => {
        entry.cancelClick?.();
        event.preventDefault();
        // Flickity finishes its touch gesture on window's touchend listener.
        // Let double-tap events reach it even when the player moves to fullscreen.
        if (event.type !== 'touchend') event.stopPropagation();
        try {
          if (placement) {
            restore();
          } else if ((document.fullscreenElement || document.webkitFullscreenElement) === host) {
            await (document.exitFullscreen || document.webkitExitFullscreen).call(document);
          } else {
            await (host.requestFullscreen || host.webkitRequestFullscreen).call(host);
          }
        } catch (_) {
          // iOS and embedded browsers may refuse element fullscreen.
          // Use the same player and controls in a viewport-filling stage.
          const box = host.getBoundingClientRect();
          const placeholder = document.createElement('div');
          placeholder.style.width = box.width + 'px';
          placeholder.style.height = box.height + 'px';
          host.before(placeholder);
          placement = { placeholder };
          document.body.append(host);
          host.classList.add('bison-video-fullscreen-fallback');
          sync();
        }
      };
      expand.addEventListener('click', toggleFullscreen);
      exit.addEventListener('click', toggleFullscreen);
      host.addEventListener('dblclick', event => {
        if (Date.now() - (entry.touchFullscreenAt || 0) < 600) return;
        if (!event.target.closest('button, a, input, .bison-video-share-menu')) toggleFullscreen(event);
      });
      // Handle double-tap explicitly on touch screens without waiting to start audio.
      let lastTap = null;
      media.addEventListener('touchend', event => {
        if (event.changedTouches.length !== 1) { lastTap = null; return; }
        const touch = event.changedTouches[0], now = Date.now();
        if (lastTap && now - lastTap.time < 320 &&
            Math.hypot(touch.clientX - lastTap.x, touch.clientY - lastTap.y) < 30) {
          lastTap = null;
          entry.touchFullscreenAt = now;
          toggleFullscreen(event);
        } else lastTap = { time: now, x: touch.clientX, y: touch.clientY };
      }, { passive: false });
    }
    if (media.tagName === 'VIDEO' && media.controls && !media.loop) {
      // Initialize accessible controls before removing the browser's overlays.
      const bar = document.createElement('div');
      bar.className = 'bison-player-controls';
      bar.setAttribute('role', 'group');
      bar.setAttribute('aria-label', 'Video controls');
      const play = document.createElement('button');
      play.type = 'button';
      play.className = 'bison-player-play';
      const timeline = document.createElement('input');
      timeline.type = 'range'; timeline.min = '0'; timeline.max = '1000'; timeline.value = '0';
      timeline.step = '1'; timeline.className = 'bison-player-timeline';
      timeline.setAttribute('aria-label', 'Video progress');
      const clock = document.createElement('span');
      clock.className = 'bison-player-time';
      const sound = document.createElement('button');
      sound.type = 'button'; sound.className = 'bison-player-sound'; sound.textContent = 'SOUND';
      const captions = document.createElement('button');
      captions.type = 'button'; captions.className = 'bison-player-cc'; captions.textContent = 'CC';
      const captionTracks = () => Array.from(media.textTracks).filter(track =>
        track.kind === 'captions' || track.kind === 'subtitles');
      const syncCaptions = () => {
        const tracks = captionTracks();
        const enabled = tracks.some(track => track.mode === 'showing');
        captions.hidden = tracks.length === 0;
        captions.classList.toggle('is-off', !enabled);
        captions.setAttribute('aria-pressed', String(enabled));
        captions.setAttribute('aria-label', enabled ? 'Turn captions off' : 'Turn captions on');
      };
      captions.addEventListener('click', () => {
        entry.cancelClick?.();
        const tracks = captionTracks();
        const enabled = tracks.some(track => track.mode === 'showing');
        const selected = tracks.find(track => track.mode === 'showing') || tracks[0];
        // Safari requires default on the selected track with custom video controls.
        if (!enabled) {
          const element = Array.from(media.querySelectorAll('track')).find(el => el.track === selected);
          if (element) element.default = true;
        }
        tracks.forEach(track => { track.mode = !enabled && track === selected ? 'showing' : 'hidden'; });
        syncCaptions();
      });
      media.textTracks.addEventListener('change', syncCaptions);
      media.textTracks.addEventListener('addtrack', syncCaptions);
      media.textTracks.addEventListener('removetrack', syncCaptions);
      syncCaptions();
      const time = seconds => {
        const value = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
        return Math.floor(value / 60) + ':' + String(value % 60).padStart(2, '0');
      };
      let scrubbing = false;
      const syncPlayer = () => {
        play.textContent = media.paused ? '▶' : 'Ⅱ';
        play.setAttribute('aria-label', media.paused ? 'Play video' : 'Pause video');
        sound.classList.toggle('is-muted', media.muted);
        sound.setAttribute('aria-pressed', String(!media.muted));
        sound.setAttribute('aria-label', media.muted ? 'Turn sound on' : 'Turn sound off');
        const duration = media.duration;
        timeline.disabled = !Number.isFinite(duration) || duration <= 0;
        if (!scrubbing)
          timeline.value = timeline.disabled ? '0' : String(Math.round(media.currentTime / duration * 1000));
        timeline.setAttribute('aria-valuetext', time(media.currentTime) + ' of ' + time(duration));
        clock.textContent = time(media.currentTime) + ' / ' + time(duration);
      };
      play.addEventListener('click', () => {
        entry.cancelClick?.();
        if (media.paused) media.play().catch(() => {});
        else media.pause();
      });
      sound.addEventListener('click', () => { media.muted = !media.muted; });
      timeline.addEventListener('input', () => {
        entry.cancelClick?.();
        if (Number.isFinite(media.duration) && media.duration > 0)
          media.currentTime = Number(timeline.value) / 1000 * media.duration;
      });
      bar.addEventListener('click', event => event.stopPropagation());
      bar.addEventListener('dblclick', event => event.stopPropagation());
      for (const event of ['loadedmetadata', 'durationchange', 'timeupdate', 'play', 'pause', 'ended', 'volumechange'])
        media.addEventListener(event, syncPlayer);
      bar.append(play, timeline, clock, captions, sound);
      host.append(bar);
      host.classList.add('bison-player-custom');
      let hideProgressTimer = null;
      const revealProgress = () => {
        clearTimeout(hideProgressTimer);
        host.classList.remove('bison-player-progress-hidden');
        hideProgressTimer = setTimeout(() => {
          // Keep the seek line available while a keyboard user is interacting.
          if (!scrubbing && !bar.contains(document.activeElement))
            host.classList.add('bison-player-progress-hidden');
        }, 3000);
      };
      // Let the native range handle dragging without playback updates moving its thumb.
      timeline.addEventListener('pointerdown', () => {
        scrubbing = true;
        entry.cancelClick?.();
        revealProgress();
      });
      const finishScrubbing = event => {
        if (!scrubbing) return;
        scrubbing = false;
        syncPlayer();
        if (event.pointerType === 'touch') timeline.blur();
        revealProgress();
      };
      document.addEventListener('pointerup', finishScrubbing);
      document.addEventListener('pointercancel', finishScrubbing);
      host.addEventListener('touchstart', revealProgress, { passive: true });
      host.addEventListener('pointerdown', revealProgress);
      host.addEventListener('pointermove', revealProgress);
      bar.addEventListener('input', revealProgress);
      bar.addEventListener('focusin', revealProgress);
      bar.addEventListener('focusout', revealProgress);
      media.addEventListener('play', revealProgress);
      document.addEventListener('fullscreenchange', revealProgress);
      document.addEventListener('webkitfullscreenchange', revealProgress);
      revealProgress();
      media.controls = false;
      media.tabIndex = 0;
      media.addEventListener('keydown', event => {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault(); entry.cancelClick?.(); play.click();
        }
      });
      syncPlayer();
    }
    if (media.tagName === 'VIDEO' && !media.loop &&
        !button.classList.contains('bison-video-share--background')) {
      const card = document.createElement('div');
      card.className = 'bison-video-end-card';
      card.hidden = true;
      card.setAttribute('role', 'group');
      card.setAttribute('aria-label', 'Video finished');
      const heading = document.createElement('strong');
      heading.textContent = 'Share or watch again';
      const actions = document.createElement('div');
      const replay = document.createElement('button');
      replay.type = 'button'; replay.textContent = 'REPLAY';
      replay.addEventListener('click', event => {
        event.stopPropagation();
        card.hidden = true;
        media.currentTime = 0;
        media.play().catch(() => { card.hidden = false; });
      });
      const share = document.createElement('button');
      share.type = 'button'; share.textContent = 'SHARE';
      share.addEventListener('click', event => {
        event.stopPropagation();
        button.click();
      });
      actions.append(share, replay);
      card.append(heading, actions);
      host.append(card);
      media.addEventListener('ended', () => {
        // The mobile landscape player moves out of the gallery.
        media.parentElement.append(card);
        card.hidden = false;
      });
      media.addEventListener('play', () => { card.hidden = true; });
      media.addEventListener('seeking', () => { if (!media.ended) card.hidden = true; });
    }
    button.addEventListener('click', event => {
      event.stopPropagation();
      if (panel && !panel.hidden && opener === button) { close(true); return; }
      const mobile = matchMedia('(pointer: coarse)').matches;
      if (mobile && navigator.share) { opener = button; nativeShare(entry); }
      else show(entry);
    });
  };
  const scan = root => {
    if (root.matches?.(selector)) enhance(root);
    root.querySelectorAll?.(selector).forEach(enhance);
  };
  let revealedHash = '';
  let sharedEntry = null;
  const scrollToSharedVideo = () => {
    if (!sharedEntry?.media.isConnected || sharedEntry.media.closest('[hidden]')) return;
    const box = sharedEntry.media.getBoundingClientRect();
    if (!box.width || !box.height) return;
    window.scrollTo({ top: Math.max(0, window.scrollY + box.top - (window.innerHeight - box.height) / 2), behavior: 'instant' });
  };
  const reveal = () => {
    const hash = location.hash;
    if (!hash.startsWith('#bison-video-') || hash === revealedHash) return;
    const key = hash.slice(13);
    const matches = Array.from(entries.values()).filter(entry => entry.key === key);
    if (!matches.length) return;
    // Shopify has separate mobile and desktop players for the same asset.
    const entry = matches.find(({media}) => !media.closest('.small--hide') && matchMedia('(max-width: 768px)').matches) ||
      matches.find(({media}) => !media.closest('.medium-up--hide') && !matchMedia('(max-width: 768px)').matches) || matches[0];
    const media = entry.media;
    const carousel = media.closest('[data-bsib-carousel], [data-bison-video-carousel], [data-bnl-video-carousel]');
    const slide = media.closest('[role="tabpanel"], .bsib__slide, .bnl-video-slide');
    if (carousel && slide) carousel.dispatchEvent(new CustomEvent('bison:video-reveal', { detail: { slide } }));
    const productSlide = media.closest('.product-main-slide');
    if (productSlide) {
      const gallery = productSlide.closest('[data-product-images]');
      const index = productSlide.getAttribute('data-index');
      const thumbnail = Array.from(gallery?.querySelectorAll('[data-product-thumb]') || []).find(thumb => thumb.getAttribute('data-index') === index);
      thumbnail?.click();
      if (matchMedia('(max-width: 768px)').matches) {
        productSlide.classList.add('bison-mobile-inline-active'); media.classList.remove('hide');
      }
    }
    const modal = media.closest('#BisonIntroVideoModal');
    if (modal) modal.dispatchEvent(new CustomEvent('bison:video-reveal'));
    revealedHash = hash;
    sharedEntry = entry;
    requestAnimationFrame(() => {
      if (media.tagName === 'VIDEO') media.pause();
      // Focus a regular control rather than Safari's native video surface.
      entry.button.focus({ preventScroll: true });
      scrollToSharedVideo();
    });
    window.dataLayer?.push({ event: 'video_shared_visit', content_type: 'video', item_id: key });
  };
  let initialized = false;
  const init = () => {
    if (initialized) return;
    initialized = true;
    scan(document);
    // Run after deferred section scripts have registered their carousel hooks.
    setTimeout(reveal, 0);
    // Initial navigation and Shopify's preview bar can restore scroll at load.
    // Reposition once the full page has settled, including on browser reload.
    const settle = () => setTimeout(scrollToSharedVideo, 150);
    if (document.readyState === 'complete') settle();
    else window.addEventListener('load', settle, { once: true });
    new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes') scan(mutation.target);
        else mutation.addedNodes.forEach(node => { if (node.nodeType === 1) scan(node); });
      }
      reveal();
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'data-src', 'title'] });
    document.addEventListener('click', event => {
      if (panel && !panel.hidden && !panel.contains(event.target) && !event.target.closest('.bison-video-share')) close();
    });
    document.addEventListener('keydown', event => {
      if (!panel || panel.hidden) return;
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(true); }
      if (event.key === 'Tab') {
        const items = Array.from(panel.querySelectorAll('button, a[href], input:not([hidden])'));
        const first = items[0], last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    }, true);
    window.addEventListener('hashchange', () => { revealedHash = ''; reveal(); });
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, { passive: true });
  };
  // Deferred scripts run with readyState=interactive before later carousel
  // scripts have registered their reveal handlers. Wait for all of them.
  if (document.readyState === 'complete') init();
  else {
    document.addEventListener('DOMContentLoaded', init, { once: true });
    window.addEventListener('load', init, { once: true });
  }
})();
